import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { money, resizeLogo } from '../events/util';

export default function TpvMenus() {
  const api = useApi();
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [products, setProducts] = useState([]);
  const [creatingMenu, setCreatingMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);      // renombrar el menú activo
  const [renameVal, setRenameVal] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [menuName, setMenuName] = useState('');
  const [menuType, setMenuType] = useState('barra');
  const [np, setNp] = useState({ name: '', category: 'General', price: '' });
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [editing, setEditing] = useState(null); // producto en edición
  const [busca, setBusca] = useState('');        // filtro de la lista de productos
  const activeMenuObj = menus.find(m => m.id === activeMenu) || null;
  const isBoleteria = activeMenuObj?.type === 'boleteria';

  // Productos visibles según el buscador. Compara sin acentos, así "cafe" encuentra
  // "Café" (nadie escribe los acentos en un buscador).
  //
  // El rango de diacríticos se arma con escapes y no con los caracteres literales:
  // escritos crudos son invisibles en el archivo y cualquier herramienta que toque el
  // encoding los rompe sin que se note.
  const DIACRITICOS = new RegExp('[\\u0300-\\u036f]', 'g');
  const norm = (t) => String(t || '').toLowerCase().normalize('NFD').replace(DIACRITICOS, '');
  const visibles = busca.trim()
    ? products.filter(p => norm(p.name).includes(norm(busca.trim())) || norm(p.category).includes(norm(busca.trim())))
    : products;

  const loadMenus = async () => {
    const m = await api.get('/api/tpv/menus').catch(() => []);
    setMenus(m);
    if (m.length && !m.find(x => x.id === activeMenu)) setActiveMenu(m[0].id);
    if (!m.length) setActiveMenu(null);
  };
  const loadProducts = (menuId) => api.get(`/api/tpv/products?menu_id=${menuId}`).then(setProducts).catch(() => {});
  const loadCategories = (menuId) => api.get(`/api/tpv/categories?menu_id=${menuId}`).then(setCategories).catch(() => {});

  useEffect(() => { loadMenus(); }, []); // eslint-disable-line
  useEffect(() => { if (activeMenu) { loadProducts(activeMenu); loadCategories(activeMenu); } else { setProducts([]); setCategories([]); } setBusca(''); }, [activeMenu]); // eslint-disable-line

  async function createMenu() {
    if (!menuName.trim()) return;
    try { const m = await api.post('/api/tpv/menus', { name: menuName, type: menuType }); setMenuName(''); setMenuType('barra'); setCreatingMenu(false); await loadMenus(); setActiveMenu(m.id); }
    catch (e) { alert(e.message); }
  }
  async function deleteMenu() {
    if (!window.confirm('¿Eliminar este menú y todos sus productos?')) return;
    try { await api.del(`/api/tpv/menus/${activeMenu}`); await loadMenus(); } catch (e) { alert(e.message); }
  }
  // Renombrar el menú. Solo cambia el nombre: no toca productos, categorías, el
  // diseño del ticket ni las cajas que ya lo tienen asignado.
  async function saveRename() {
    const n = renameVal.trim();
    if (!n) return;
    setSavingName(true);
    try {
      await api.patch(`/api/tpv/menus/${activeMenu}`, { name: n });
      setRenaming(false);
      await loadMenus();
    } catch (e) { alert(e.message); }
    finally { setSavingName(false); }
  }
  async function addProduct() {
    if (!np.name.trim() || np.price === '') { alert('Nombre y precio requeridos'); return; }
    try { await api.post('/api/tpv/products', { menu_id: activeMenu, name: np.name, category: np.category || 'General', price: +np.price || 0 }); setNp({ name: '', category: np.category || 'General', price: '' }); loadProducts(activeMenu); }
    catch (e) { alert(e.message); }
  }
  async function removeProduct(id) {
    try { await api.del(`/api/tpv/products/${id}`); loadProducts(activeMenu); } catch (e) { alert(e.message); }
  }
  async function saveProduct() {
    if (!editing.name.trim() || editing.price === '') { alert('Nombre y precio requeridos'); return; }
    try { await api.patch(`/api/tpv/products/${editing.id}`, { name: editing.name, category: editing.category || 'General', price: +editing.price || 0 }); setEditing(null); loadProducts(activeMenu); }
    catch (e) { alert(e.message); }
  }
  async function addCategory() {
    const n = newCat.trim(); if (!n) return;
    try { await api.post('/api/tpv/categories', { menu_id: activeMenu, name: n }); setNewCat(''); loadCategories(activeMenu); }
    catch (e) { alert(e.message); }
  }
  async function deleteCategory(c) {
    if (c.name === 'General') return;
    if (!window.confirm(`¿Borrar la categoría "${c.name}"? Sus productos pasan a General.`)) return;
    try { await api.del(`/api/tpv/categories/${c.id}`); loadCategories(activeMenu); loadProducts(activeMenu); }
    catch (e) { alert(e.message); }
  }

  return (
    <div className="lb-fade">
      <div className="lb-display" style={{ fontSize: 24, marginBottom: 6 }}>Menús de barra</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 14, marginTop: 0, marginBottom: 22 }}>Creá los menús que después asignás a cada caja TPV del evento.</p>

      {/* pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
        {menus.map(m => (
          <button key={m.id} onClick={() => setActiveMenu(m.id)} className={'lb-btn lb-btn--sm ' + (activeMenu === m.id ? 'lb-btn--white' : 'lb-btn--ghost')}>
            {m.name}{m.type === 'guardarropa' ? ' · 🧥' : m.type === 'boleteria' ? ' · 🎟️' : ''}
          </button>
        ))}
        {creatingMenu ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input className="lb-input" style={{ padding: '8px 12px', fontSize: 13, width: 180 }} value={menuName} autoFocus onChange={e => setMenuName(e.target.value)} placeholder="Nombre del menú" onKeyDown={e => e.key === 'Enter' && createMenu()} />
            <select className="lb-select" style={{ padding: '8px 28px 8px 12px', fontSize: 13, width: 150 }} value={menuType} onChange={e => setMenuType(e.target.value)}>
              <option value="barra">Barra</option>
              <option value="guardarropa">Guardarropa</option>
              <option value="boleteria">Boletería</option>
            </select>
            <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={createMenu}>Crear</button>
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setCreatingMenu(false)}>✕</button>
          </div>
        ) : (
          <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setCreatingMenu(true)}>+ Crear menú</button>
        )}
      </div>

      {/* Renombrar el menú seleccionado. El nombre es lo que ve el dueño al asignar
          la caja, así que tiene que poder corregirlo sin borrar y volver a crear
          (borrar el menú se lleva sus productos y categorías). */}
      {activeMenu && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
          {renaming ? (
            <>
              <span className="lb-eyebrow">Nombre del menú</span>
              <input
                className="lb-input" style={{ padding: '8px 12px', fontSize: 13, width: 220 }}
                value={renameVal} autoFocus
                onChange={e => setRenameVal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(false); }}
              />
              <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={saveRename} disabled={!renameVal.trim() || savingName}>
                {savingName ? 'Guardando…' : 'Guardar'}
              </button>
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setRenaming(false)}>Cancelar</button>
            </>
          ) : (
            <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { setRenameVal(activeMenuObj?.name || ''); setRenaming(true); }}>
              ✎ Cambiar nombre del menú
            </button>
          )}
        </div>
      )}

      {!activeMenu && <div style={{ color: 'var(--ink-600)', fontSize: 13 }}>Creá tu primer menú para empezar a cargar productos.</div>}

      {activeMenu && (
        <>
          {isBoleteria && (
            <div className="lb-card" style={{ padding: '20px 24px', marginBottom: 16, border: '1px solid rgba(236,38,143,0.35)', background: 'rgba(236,38,143,0.05)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#EC268F', fontSize: 14, marginBottom: 6 }}>La boletería no usa productos</div>
              <div style={{ fontSize: 13.5, color: 'var(--ink-700)', lineHeight: 1.6 }}>
                Lo que vende la boletería son los <b style={{ color: '#fff' }}>lotes</b> que ya creaste (en Grupos de Lotes o en el evento) con el medio de venta <b style={{ color: '#fff' }}>Boletería</b> — por ejemplo INGRESO RRPP, INGRESO CUMPLEAÑERO, PUERTA GENERAL, etc. Acá no cargás nada: al crear la <b style={{ color: '#fff' }}>caja de boletería</b> en el evento (pestaña Cajas), esos lotes aparecen solos (por defecto vende todos; podés acotar a un subconjunto). Este menú solo define el <b style={{ color: '#fff' }}>diseño del ticket</b>, que configurás abajo.
              </div>
            </div>
          )}
          {!isBoleteria && (<>
          {/* Categorías del menú */}
          <div className="lb-card" style={{ padding: '14px 24px', marginBottom: 16 }}>
            <div className="lb-eyebrow" style={{ marginBottom: 10 }}>Categorías</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              {categories.map(c => (
                <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--ink-200)', border: '1px solid var(--border)', borderRadius: 4, padding: '5px 10px', fontSize: 13, color: '#fff' }}>
                  {c.name}
                  {c.name !== 'General' && <button className="lb-iconbtn" style={{ width: 18, height: 18, fontSize: 11 }} onClick={() => deleteCategory(c)} title="Borrar categoría">✕</button>}
                </span>
              ))}
              <input className="lb-input" style={{ padding: '6px 10px', fontSize: 13, width: 160 }} value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Nueva categoría" onKeyDown={e => e.key === 'Enter' && addCategory()} />
              <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={addCategory}>+ Agregar</button>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-600)' }}>Creá las categorías antes de los productos. Al cargar un producto la elegís del desplegable; por defecto queda en General.</div>
          </div>

          {/* Buscador del menú: con la carta entera cargada, encontrar un producto para
              corregirle el precio significaba recorrer la lista a ojo. Busca por nombre
              y también por categoría, así "cerveza" trae todas las de esa categoría. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
            <input className="lb-input" style={{ maxWidth: 300 }} value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder={`Buscar producto en ${products.length} de este menú`} />
            {busca.trim() && (
              <>
                <span style={{ fontSize: 12, color: 'var(--ink-700)' }}>{visibles.length} de {products.length}</span>
                <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setBusca('')}>Limpiar</button>
              </>
            )}
          </div>

          <div className="lb-card" style={{ padding: '6px 24px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 120px' }}>
              <span>Producto</span><span>Categoría</span><span>Precio</span><span></span>
            </div>
            {visibles.map(p => (
              <div key={p.id} className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 120px' }}>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{p.name}</span>
                <span style={{ color: 'var(--ink-700)', fontSize: 13 }}>{p.category}</span>
                <span style={{ color: 'var(--ink-800)', fontFamily: 'var(--font-display)' }}>{money(p.price)}</span>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setEditing({ ...p, price: String(p.price) })}>Editar</button>
                  <button className="lb-iconbtn" onClick={() => removeProduct(p.id)}>✕</button>
                </span>
              </div>
            ))}
            {products.length === 0 && <div style={{ padding: '18px 0', fontSize: 13, color: 'var(--ink-600)' }}>Sin productos en este menú.</div>}
            {products.length > 0 && visibles.length === 0 && <div style={{ padding: '18px 0', fontSize: 13, color: 'var(--ink-600)' }}>Ningún producto coincide con "{busca}".</div>}
            {/* add row */}
            <div className="lb-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr 120px', borderTop: '1px dashed rgba(255,255,255,0.16)' }}>
              <input className="lb-input" style={{ padding: '10px 12px', fontSize: 13 }} placeholder="Nombre" value={np.name} onChange={e => setNp({ ...np, name: e.target.value })} />
              <select className="lb-select" style={{ padding: '10px 12px', fontSize: 13 }} value={np.category} onChange={e => setNp({ ...np, category: e.target.value })}>
                {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <input className="lb-input" style={{ padding: '10px 12px', fontSize: 13 }} placeholder="Precio" inputMode="numeric" value={np.price} onChange={e => setNp({ ...np, price: e.target.value.replace(/\D/g, '') })} />
              <button className="lb-btn lb-btn--white lb-btn--sm" onClick={addProduct}>+ Agregar</button>
            </div>
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>Stock vacío = ilimitado (∞)</div>
          </>)}

          <TicketConfig menu={menus.find(m => m.id === activeMenu)} api={api} onSaved={loadMenus} />
          <FiscalConfig menu={menus.find(m => m.id === activeMenu)} api={api} onSaved={loadMenus} />

          <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginTop: 18 }} onClick={deleteMenu}>Eliminar menú</button>

          {editing && (
            <div className="lb-modal-backdrop" onClick={() => setEditing(null)}>
              <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: 24 }}>
                <div className="lb-h" style={{ fontSize: 18, marginBottom: 14 }}>Editar producto</div>
                <Field label="Nombre"><input className="lb-input" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></Field>
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <Field label="Categoría">
                    <select className="lb-select" value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })}>
                      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      {!categories.some(c => c.name === editing.category) && <option value={editing.category}>{editing.category}</option>}
                    </select>
                  </Field>
                  <Field label="Precio"><input className="lb-input" inputMode="numeric" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value.replace(/\D/g, '') })} /></Field>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button className="lb-btn lb-btn--primary lb-btn--block" onClick={saveProduct}>Guardar</button>
                  <button className="lb-btn lb-btn--ghost lb-btn--block" onClick={() => setEditing(null)}>Cancelar</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TicketConfig({ menu, api, onSaved }) {
  const [cfg, setCfg] = useState({ ticket_logo: null, ticket_header_type: 'event', ticket_header_text: '', ticket_footer: '' });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!menu) return;
    setCfg({
      ticket_logo: menu.ticket_logo || null,
      ticket_header_type: menu.ticket_header_type || 'event',
      ticket_header_text: menu.ticket_header_text || '',
      ticket_footer: menu.ticket_footer || '',
    });
  }, [menu]);

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await resizeLogo(file, 400); setCfg(c => ({ ...c, ticket_logo: url })); } catch (err) { alert(err.message); }
  };
  async function save() {
    try { await api.patch(`/api/tpv/menus/${menu.id}`, cfg); setSaved(true); setTimeout(() => setSaved(false), 2000); onSaved && onSaved(); }
    catch (e) { alert(e.message); }
  }

  if (!menu) return null;
  return (
    <div className="lb-card" style={{ padding: '20px 24px', marginTop: 22 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Configuración del ticket</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 12, marginTop: 0 }}>Cada caja que use este menú imprime el ticket con este logo, encabezado y pie.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginTop: 12 }} className="tk-2col">
        <Field label="Logo del ticket (PNG transparente, logo en NEGRO)">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 56, height: 56, borderRadius: 4, background: cfg.ticket_logo ? `center/contain no-repeat #fff url(${cfg.ticket_logo})` : 'var(--ink-200)', border: '1px solid var(--border)', flex: 'none' }} title="Previsualización sobre papel blanco" />
            <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ cursor: 'pointer' }}>{cfg.ticket_logo ? 'Cambiar logo' : 'Subir logo'}<input type="file" accept="image/png,image/*" onChange={onLogo} style={{ display: 'none' }} /></label>
            {cfg.ticket_logo && <button className="lb-iconbtn" onClick={() => setCfg(c => ({ ...c, ticket_logo: null }))} title="Quitar">✕</button>}
          </div>
        </Field>

        <Field label="Pie de página del ticket">
          <input className="lb-input" value={cfg.ticket_footer} onChange={e => setCfg(c => ({ ...c, ticket_footer: e.target.value }))} placeholder="¡Gracias por tu compra!" />
        </Field>

        <div style={{ gridColumn: '1 / -1' }}>
          <div className="lb-label" style={{ marginBottom: 8 }}>Encabezado del ticket</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <button className={'lb-btn lb-btn--sm ' + (cfg.ticket_header_type === 'event' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setCfg(c => ({ ...c, ticket_header_type: 'event' }))}>Nombre del evento</button>
            <button className={'lb-btn lb-btn--sm ' + (cfg.ticket_header_type === 'custom' ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setCfg(c => ({ ...c, ticket_header_type: 'custom' }))}>Frase personalizada</button>
          </div>
          {cfg.ticket_header_type === 'custom' && (
            <input className="lb-input" value={cfg.ticket_header_text} onChange={e => setCfg(c => ({ ...c, ticket_header_text: e.target.value }))} placeholder="Ej: BARRA CENTRAL" />
          )}
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save}>Guardar configuración del ticket</button>
        {saved && <span style={{ color: '#2ecc71', fontSize: 12 }}>Guardado ✓</span>}
      </div>
    </div>
  );
}

// LO FISCAL DEL PUESTO. Se define una vez acá y lo heredan todas las cajas que lo usen,
// porque las cajas se crean nuevas para cada noche. Nace apagado: muchos negocios ya
// facturan el mostrador por otra vía y no quieren que el sistema emita nada.
function FiscalConfig({ menu, api, onSaved }) {
  const [fiscal, setFiscal] = useState(false);
  const [scope, setScope] = useState('qr');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!menu) return;
    setFiscal(!!menu.fiscal);
    setScope(menu.fiscal_scope || 'qr');
  }, [menu]);

  async function save() {
    try { await api.patch(`/api/tpv/menus/${menu.id}`, { fiscal, fiscal_scope: scope }); setSaved(true); setTimeout(() => setSaved(false), 2000); onSaved && onSaved(); }
    catch (e) { alert(e.message); }
  }

  if (!menu) return null;
  return (
    <div className="lb-card" style={{ padding: '20px 24px', marginTop: 22 }}>
      <div className="lb-eyebrow" style={{ marginBottom: 4 }}>Facturación de este puesto</div>
      <p style={{ color: 'var(--ink-700)', fontSize: 12, marginTop: 0, lineHeight: 1.6 }}>
        Al cobrar, el cajero elige efectivo o QR. Acá decidís si lo que cobra este puesto emite comprobante, y sobre qué.
        Vale para todas las cajas que usen este puesto.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        {[[true, 'Caja fiscal'], [false, 'No factura']].map(([v, t]) => (
          <button key={String(v)} className={'lb-btn lb-btn--sm ' + (fiscal === v ? 'lb-btn--white' : 'lb-btn--ghost')} onClick={() => setFiscal(v)}>{t}</button>
        ))}
      </div>

      {fiscal && (
        <div style={{ marginTop: 16 }}>
          <div className="lb-label" style={{ marginBottom: 8 }}>¿Qué factura?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 440 }}>
            {[['qr', 'Solo lo cobrado por QR', 'El efectivo no emite comprobante desde acá'],
              ['todo', 'Todo lo que cobra la caja', 'También las ventas en efectivo']].map(([k, t, d]) => (
              <button key={k} type="button" onClick={() => setScope(k)}
                style={{ textAlign: 'left', cursor: 'pointer', padding: '11px 13px', borderRadius: 4,
                  background: scope === k ? 'rgba(255,255,255,0.06)' : 'var(--ink-050)',
                  border: '1px solid ' + (scope === k ? 'rgba(255,255,255,0.5)' : 'var(--border)') }}>
                <div style={{ color: scope === k ? '#fff' : 'var(--ink-800)', fontWeight: 600, fontSize: 13 }}>{t}</div>
                <div style={{ color: 'var(--ink-600)', fontSize: 11.5, marginTop: 2 }}>{d}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={save}>Guardar</button>
        {saved && <span style={{ color: '#2ecc71', fontSize: 12 }}>Guardado ✓</span>}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 12, lineHeight: 1.6 }}>
        El CUIT, el punto de venta y qué entradas se facturan se cargan en <b style={{ color: 'var(--ink-800)' }}>Facturación ARCA</b>.
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>; }
